import {
  buildPriceKey,
  checkCostAgainstMarket,
  HoldingValidationError,
  validateHoldingDraft,
  type AssetType,
  type Holding,
  type HoldingDraft,
  type ValidatedHolding,
} from '@porttang/core';
import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { useLookupPrice, useSearchAssets, type AssetHit } from '../prices/usePrices';

const ASSET_TYPE_LABELS: Record<AssetType, string> = {
  stock: 'หุ้น',
  etf: 'ETF',
  crypto: 'คริปโต',
  cash: 'เงินสด',
};

/** ค่าดิบจากฟอร์ม — เป็น string ทั้งหมดเพราะ input ให้มาแบบนั้น */
interface FormValues {
  platform: string;
  assetType: AssetType;
  symbol: string;
  coingeckoId: string;
  displayName: string;
  quantity: string;
  avgCost: string;
  currency: string;
  note: string;
}

function initialValues(holding: Holding | null): FormValues {
  if (holding === null) {
    return {
      platform: '',
      assetType: 'etf',
      symbol: '',
      coingeckoId: '',
      displayName: '',
      quantity: '',
      avgCost: '',
      currency: 'USD',
      note: '',
    };
  }

  return {
    platform: holding.platform,
    assetType: holding.assetType,
    symbol: holding.symbol,
    // priceKey ของคริปโตเก็บเป็น `coingecko:<id>` — ดึง id กลับมาให้แก้ต่อได้
    coingeckoId: holding.priceKey.startsWith('coingecko:')
      ? holding.priceKey.slice('coingecko:'.length)
      : '',
    displayName: holding.displayName,
    quantity: holding.quantity,
    avgCost: holding.avgCost,
    currency: holding.currency,
    note: holding.note ?? '',
  };
}

function toDraft(values: FormValues): HoldingDraft {
  return {
    platform: values.platform,
    assetType: values.assetType,
    symbol: values.symbol,
    coingeckoId: values.coingeckoId,
    displayName: values.displayName,
    quantity: values.quantity,
    avgCost: values.avgCost,
    currency: values.currency,
    note: values.note,
  };
}

const inputClass =
  'w-full rounded-md border border-line-strong bg-sunken px-3 py-2 text-ink-100 outline-none focus:border-brand';

function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string;
  error?: string | undefined;
  children: ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1 block text-sm text-ink-200">{label}</span>
      {children}
      {hint !== undefined && error === undefined && (
        <span className="mt-1 block text-xs text-ink-500">{hint}</span>
      )}
      {error !== undefined && (
        <span role="alert" className="mt-1 block text-xs text-down">
          {error}
        </span>
      )}
    </label>
  );
}

/**
 * ช่องค้นหาสินทรัพย์เหนือช่องสัญลักษณ์
 *
 * มีไว้แก้ปัญหาที่ฟอร์มเดิมโยนให้ผู้ใช้: ช่อง "รหัสเหรียญ CoinGecko" คาดหวังให้รู้ว่า
 * ADA คือ `cardano` ซึ่งไม่มีทางรู้ถ้าไม่เคยเปิด API doc — ผลคือเพิ่มเหรียญเข้าพอร์ต
 * แล้วไม่มีราคาขึ้น โดยไม่มีอะไรบอกว่าต้องพิมพ์อะไรถึงจะถูก
 *
 * **เป็นตัวช่วย ไม่ใช่ทางเดียว** — ช่องพิมพ์เองยังอยู่ครบและบันทึกได้ตามปกติ
 * เพราะ Yahoo search เป็น endpoint ที่ไม่เป็นทางการ วันหนึ่งมันหายไปได้
 */
function AssetSearchField({
  assetType,
  onPick,
}: {
  assetType: AssetType;
  onPick: (hit: AssetHit) => void;
}) {
  const [term, setTerm] = useState('');
  const [debounced, setDebounced] = useState('');

  // หน่วงด้วยเหตุผลเดียวกับการดูราคา — ยิงทุกตัวอักษรคือทางตรงไปหา rate limit
  // ของ CoinGecko ซึ่งใช้โควตาก้อนเดียวกับการดึงราคาทั้งพอร์ต
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(term), 600);
    return () => clearTimeout(timer);
  }, [term]);

  const search = useSearchAssets(debounced, assetType, term.trim().length > 0);
  const hits = search.data ?? [];

  function pick(hit: AssetHit) {
    onPick(hit);
    // เคลียร์คำค้นทิ้ง — ค่าที่เลือกไปโผล่ในช่องข้างล่างแล้ว รายการค้างไว้ก็รก
    setTerm('');
    setDebounced('');
  }

  return (
    <Field
      label="ค้นหาสินทรัพย์"
      hint={
        assetType === 'crypto'
          ? 'พิมพ์ชื่อหรือสัญลักษณ์ เช่น ADA, bitcoin แล้วเลือกจากรายการ'
          : 'พิมพ์ชื่อหรือ ticker เช่น vanguard, AAPL แล้วเลือกจากรายการ'
      }
    >
      <input
        className={inputClass}
        value={term}
        onChange={(event) => setTerm(event.target.value)}
        placeholder="ค้นหา…"
        autoFocus
      />

      {term.trim().length > 0 && (
        <div className="mt-2">
          {search.isFetching && <p className="text-xs text-ink-500">กำลังค้นหา…</p>}

          {search.isError && (
            <p className="text-xs text-ink-500">
              ค้นหาไม่ได้ตอนนี้ — พิมพ์สัญลักษณ์ในช่องข้างล่างเองได้ตามปกติ
            </p>
          )}

          {!search.isFetching &&
            !search.isError &&
            hits.length === 0 &&
            debounced.trim() !== '' && (
              <p className="text-xs text-ink-500">ไม่พบสินทรัพย์ที่ตรงกับคำนี้</p>
            )}

          {hits.length > 0 && (
            <ul className="divide-y divide-line-soft overflow-hidden rounded-md border border-line-strong bg-sunken">
              {hits.map((hit) => (
                <li key={hit.id}>
                  <button
                    type="button"
                    onClick={() => pick(hit)}
                    className="flex w-full items-center gap-3 px-3 py-2 text-left transition hover:bg-hover"
                  >
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm text-ink-100">{hit.name}</span>
                      {hit.detail !== null && (
                        <span className="block truncate text-[11px] text-ink-500">
                          {hit.detail}
                        </span>
                      )}
                    </span>
                    <span className="shrink-0 font-mono text-[11px] text-ink-400">
                      {hit.symbol}
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </Field>
  );
}

/**
 * แผงแสดงราคาตลาดใต้ช่องต้นทุน
 *
 * มีไว้แก้ปัญหาที่เกิดจริง: กรอกต้นทุนผิดหน่วยไป 6 ครั้งจาก 16 (ดู PLAN.md B1)
 * บางครั้งใส่ราคาปัจจุบัน บางครั้งใส่มูลค่ารวม การเห็นราคาตลาดขณะพิมพ์
 * ทำให้สังเกตได้ทันทีว่าเลขที่กำลังจะกรอกคือเลขเดียวกันหรือคนละสเกล
 */
function MarketPriceHint({
  priceKey,
  quantity,
  avgCost,
}: {
  priceKey: string | null;
  quantity: string;
  avgCost: string;
}) {
  const lookup = useLookupPrice(priceKey);

  if (priceKey === null) {
    return null;
  }

  if (lookup.isPending) {
    return <p className="text-xs text-ink-500">กำลังดูราคาตลาด…</p>;
  }

  if (lookup.isError) {
    return <p className="text-xs text-ink-500">ดูราคาตลาดไม่ได้ตอนนี้ — บันทึกได้ตามปกติ</p>;
  }

  const result = lookup.data;
  if (result === undefined) {
    return null;
  }

  if (result.price === null) {
    return (
      <p className="rounded-md bg-warn/10 px-3 py-2 text-xs text-warn">
        ไม่พบสัญลักษณ์นี้ ({result.reason}) — บันทึกได้ แต่จะยังไม่มีราคาให้คำนวณ
      </p>
    );
  }

  const warning = checkCostAgainstMarket({
    quantity,
    avgCost,
    marketPrice: result.price,
  });

  return (
    <div className="space-y-2">
      <p className="text-xs text-ink-400">
        ราคาตลาดตอนนี้{' '}
        <span className="font-medium text-ink-100">
          {result.price} {result.currency}
        </span>
      </p>

      {warning?.kind === 'equals-market-price' && (
        <p className="rounded-md bg-warn/10 px-3 py-2 text-xs text-warn">
          ต้นทุนที่กรอกเท่ากับราคาตลาดพอดี — แน่ใจหรือไม่ว่านี่คือราคาที่ซื้อมาจริง
          ไม่ใช่ราคาปัจจุบัน
        </p>
      )}

      {warning?.kind === 'looks-like-total-value' && (
        <p className="rounded-md bg-warn/10 px-3 py-2 text-xs text-warn">
          ตัวเลขนี้ใกล้เคียง <b>มูลค่ารวม</b> ({Number(warning.totalValue).toFixed(2)}{' '}
          {result.currency}) ไม่ใช่ราคาต่อหน่วย — ถ้าใช่ ต้นทุนต่อหน่วยควรประมาณ{' '}
          <b>{Number(warning.impliedUnitCost).toFixed(2)}</b>
        </p>
      )}
    </div>
  );
}

/** ค่าพิเศษของ <option> ที่ไม่ใช่ชื่อแพลตฟอร์มจริง — ใช้สลับไปโหมดพิมพ์เอง */
const ADD_NEW_PLATFORM = '\u0000add-new';

/**
 * ช่องแพลตฟอร์ม — เลือกจากที่เคยใช้ หรือพิมพ์ชื่อใหม่ (spec Section 9.4)
 *
 * เดิมเป็นช่องพิมพ์ล้วน ทำให้พิมพ์ชื่อเดิมซ้ำทุกครั้งและพิมพ์ผิดได้โดยไม่มีใครทัก
 * ซึ่งเกิดขึ้นแล้วจริงในพอร์ตนี้ — `Jitia Wealth` กับ `Jitta Wealth` กลายเป็นสองแพลตฟอร์ม
 * แยกกันในกราฟสัดส่วน ทั้งที่เป็นที่เดียวกัน
 *
 * รายชื่อดึงจาก holdings ที่มีอยู่ ไม่ได้เก็บ collection ใหม่ — แพลตฟอร์มไม่มีตัวตน
 * ของตัวเองในระบบนี้ มันเป็นแค่ field ของ holding การตั้ง collection แยกจะกลายเป็น
 * ข้อมูลอีกชุดที่ต้องคอยลบตอนไม่มีใครใช้แล้ว
 */
function PlatformField({
  value,
  platforms,
  error,
  onChange,
}: {
  value: string;
  platforms: readonly string[];
  error: string | undefined;
  onChange: (next: string) => void;
}) {
  // ยังไม่มีอะไรให้เลือก หรือค่าที่ถืออยู่ไม่อยู่ในรายการ → เริ่มที่โหมดพิมพ์เลย
  const [typing, setTyping] = useState(
    () => platforms.length === 0 || (value !== '' && !platforms.includes(value)),
  );

  if (typing) {
    return (
      <Field
        label="แพลตฟอร์ม"
        error={error}
        hint={platforms.length === 0 ? 'เช่น IBKR, Bitkub, Dime' : undefined}
      >
        <input
          className={inputClass}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="เช่น IBKR, Bitkub, Dime"
          autoFocus
        />
        {platforms.length > 0 && (
          <button
            type="button"
            onClick={() => {
              setTyping(false);
              onChange('');
            }}
            className="mt-1.5 text-xs text-ink-400 underline transition hover:text-brand-text"
          >
            เลือกจากที่เคยใช้แทน
          </button>
        )}
      </Field>
    );
  }

  return (
    <Field label="แพลตฟอร์ม" error={error}>
      <select
        className={inputClass}
        value={value}
        onChange={(e) => {
          if (e.target.value === ADD_NEW_PLATFORM) {
            setTyping(true);
            onChange('');
            return;
          }
          onChange(e.target.value);
        }}
        autoFocus
      >
        <option value="">— เลือกแพลตฟอร์ม —</option>
        {platforms.map((platform) => (
          <option key={platform} value={platform}>
            {platform}
          </option>
        ))}
        <option value={ADD_NEW_PLATFORM}>+ เพิ่มแพลตฟอร์มใหม่…</option>
      </select>
    </Field>
  );
}

export function HoldingForm({
  holding,
  platforms,
  isSaving,
  onCancel,
  onSubmit,
}: {
  /** null = เพิ่มใหม่ */
  holding: Holding | null;
  /** แพลตฟอร์มที่เคยใช้ในพอร์ตแล้ว เรียงตามตัวอักษร */
  platforms: readonly string[];
  isSaving: boolean;
  onCancel: () => void;
  onSubmit: (validated: ValidatedHolding) => void;
}) {
  const [values, setValues] = useState<FormValues>(() => initialValues(holding));
  const [fieldError, setFieldError] = useState<{ field: string; message: string } | null>(null);
  // หน่วงไว้ก่อนยิงดูราคา ไม่งั้นจะยิงทุกตัวอักษรที่พิมพ์ลงช่องสัญลักษณ์
  const [debouncedKey, setDebouncedKey] = useState<string | null>(null);

  const priceKey = (() => {
    try {
      return buildPriceKey({
        assetType: values.assetType,
        symbol: values.symbol,
        coingeckoId: values.coingeckoId,
      });
    } catch {
      // ยังกรอกไม่ครบพอจะสร้าง key ได้ (เช่น คริปโตที่ยังไม่ใส่รหัสเหรียญ)
      return null;
    }
  })();

  useEffect(() => {
    // เงินสดไม่มีราคาให้ดู ราคาของมันคือ 1 ในสกุลของตัวเองเสมอ
    if (values.assetType === 'cash' || priceKey === null || values.symbol.trim().length === 0) {
      setDebouncedKey(null);
      return;
    }
    const timer = setTimeout(() => setDebouncedKey(priceKey), 600);
    return () => clearTimeout(timer);
  }, [priceKey, values.symbol, values.assetType]);

  function set<K extends keyof FormValues>(key: K, value: FormValues[K]) {
    setValues((current) => ({ ...current, [key]: value }));
    setFieldError((current) => (current?.field === key ? null : current));
  }

  const isCash = values.assetType === 'cash';

  function errorFor(field: string): string | undefined {
    return fieldError?.field === field ? fieldError.message : undefined;
  }

  function handleSubmit(event: FormEvent) {
    event.preventDefault();
    try {
      onSubmit(validateHoldingDraft(toDraft(values)));
    } catch (caught) {
      if (caught instanceof HoldingValidationError) {
        setFieldError({ field: caught.field, message: caught.message });
        return;
      }
      throw caught;
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/70 p-4">
      <form
        onSubmit={handleSubmit}
        className="my-8 w-full max-w-md rounded-[14px] border border-line bg-ink p-6"
      >
        <h2 className="text-lg font-semibold text-ink-100">
          {holding === null ? 'เพิ่มสินทรัพย์' : `แก้ไข ${holding.symbol}`}
        </h2>

        <div className="mt-5 space-y-4">
          <PlatformField
            value={values.platform}
            platforms={platforms}
            error={errorFor('platform')}
            onChange={(next) => set('platform', next)}
          />

          <Field label="ประเภท">
            <div className="flex gap-2">
              {(Object.keys(ASSET_TYPE_LABELS) as AssetType[]).map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => set('assetType', type)}
                  className={[
                    'flex-1 rounded-md border px-3 py-2 text-sm transition',
                    values.assetType === type
                      ? 'border-brand bg-brand/15 text-brand-text'
                      : 'border-line-strong text-ink-400 hover:border-line-dash',
                  ].join(' ')}
                >
                  {ASSET_TYPE_LABELS[type]}
                </button>
              ))}
            </div>
          </Field>

          {isCash ? (
            <>
              {/* เงินสดต้องการแค่สองอย่าง: สกุลเงินอะไร และมีเท่าไหร่
                  ไม่มีช่องต้นทุนเพราะต้นทุนของเงินสดคือ 1 เสมอโดยนิยาม —
                  ปิดช่องที่กรอกผิดได้ทิ้งไปเลยดีกว่าเตือนทีหลัง */}
              <Field label="สกุลเงิน" error={errorFor('currency')} hint="เช่น USD, THB">
                <input
                  className={`${inputClass} uppercase`}
                  value={values.currency}
                  onChange={(e) => set('currency', e.target.value)}
                />
              </Field>

              <Field
                label="จำนวนเงิน"
                error={errorFor('quantity')}
                hint="เงินสดที่ค้างอยู่ในแพลตฟอร์มนี้"
              >
                <input
                  className={inputClass}
                  inputMode="decimal"
                  value={values.quantity}
                  onChange={(e) => set('quantity', e.target.value)}
                />
              </Field>

              <Field label="ชื่อเต็ม (ไม่บังคับ)" error={errorFor('displayName')}>
                <input
                  className={inputClass}
                  value={values.displayName}
                  onChange={(e) => set('displayName', e.target.value)}
                />
              </Field>
            </>
          ) : (
            <>
              <AssetSearchField
                assetType={values.assetType}
                onPick={(hit) => {
                  setValues((current) => ({
                    ...current,
                    symbol: hit.symbol,
                    // คริปโตเก็บรหัสของ CoinGecko ส่วนหุ้น/ETF ใช้ ticker ตรง ๆ เป็น id อยู่แล้ว
                    coingeckoId: current.assetType === 'crypto' ? hit.id : current.coingeckoId,
                    // ไม่ทับชื่อที่ผู้ใช้พิมพ์เองไว้ก่อนหน้า
                    displayName: current.displayName.trim() === '' ? hit.name : current.displayName,
                  }));
                  setFieldError(null);
                }}
              />

              <Field label="สัญลักษณ์" error={errorFor('symbol')} hint="เช่น VOO, AAPL, BTC">
                <input
                  className={inputClass}
                  value={values.symbol}
                  onChange={(e) => set('symbol', e.target.value)}
                />
              </Field>

              {values.assetType === 'crypto' && (
                <Field
                  label="รหัสเหรียญ CoinGecko"
                  error={errorFor('coingeckoId')}
                  hint="ปกติช่องนี้ถูกเติมให้จากการค้นหา — กรอกเองได้ถ้ารู้ id เช่น bitcoin"
                >
                  <input
                    className={inputClass}
                    value={values.coingeckoId}
                    onChange={(e) => set('coingeckoId', e.target.value)}
                  />
                </Field>
              )}

              <Field label="ชื่อเต็ม (ไม่บังคับ)" error={errorFor('displayName')}>
                <input
                  className={inputClass}
                  value={values.displayName}
                  onChange={(e) => set('displayName', e.target.value)}
                />
              </Field>

              <div className="grid grid-cols-2 gap-3">
                <Field label="จำนวน" error={errorFor('quantity')}>
                  <input
                    className={inputClass}
                    inputMode="decimal"
                    value={values.quantity}
                    onChange={(e) => set('quantity', e.target.value)}
                  />
                </Field>

                <Field label="ต้นทุนเฉลี่ย/หน่วย" error={errorFor('avgCost')}>
                  <input
                    className={inputClass}
                    inputMode="decimal"
                    value={values.avgCost}
                    onChange={(e) => set('avgCost', e.target.value)}
                  />
                </Field>
              </div>

              <MarketPriceHint
                priceKey={debouncedKey}
                quantity={values.quantity}
                avgCost={values.avgCost}
              />

              <Field label="สกุลเงินของต้นทุน" error={errorFor('currency')} hint="เช่น USD, THB">
                <input
                  className={`${inputClass} uppercase`}
                  value={values.currency}
                  onChange={(e) => set('currency', e.target.value)}
                />
              </Field>
            </>
          )}

          <Field label="โน้ต (ไม่บังคับ)" error={errorFor('note')}>
            <textarea
              className={inputClass}
              rows={2}
              value={values.note}
              onChange={(e) => set('note', e.target.value)}
            />
          </Field>
        </div>

        <div className="mt-6 flex gap-3">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 rounded-md border border-line-strong px-4 py-2 text-ink-200 transition hover:bg-sunken"
          >
            ยกเลิก
          </button>
          <button
            type="submit"
            disabled={isSaving}
            className="flex-1 rounded-md bg-brand px-4 py-2 font-medium text-ink-100 transition hover:brightness-110 disabled:opacity-60"
          >
            {isSaving ? 'กำลังบันทึก…' : 'บันทึก'}
          </button>
        </div>
      </form>
    </div>
  );
}
